import { GroupStatusEvent, useIM, ContactState, ContactInCallStreamState, ConferenceType } from '@wui/im'
import { defineStore } from 'pinia'
import { ref } from 'vue'


import { useStoreContacts } from '@/entities/contact'
import { useIntercomRoomParticipantsStatusesStore, MemberStatus } from '@/entities/room'
import { SCREEN_SHARING_STREAM_NAME, WEBCAM_STREAM_NAME, eventBus } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { Room } from '@/shared/types/store'
import { resolveConferenceUserAgent } from '@/shared/utils/platform'
import { fromPServedUser, toPServedUser } from '@/shared/utils/uri'

import {
  useModeratedConferenceParticipantsSignalsStore,
  ContactMediaStatuses,
  ConferenceCall,
  ConferenceParticipant,
  getParticipantsFromRoom,
  AwaitingContact,
  type RoomSubscriberWithId,
} from '..'

export const useStoreConferenceMediaStatuses = defineStore('conference-media', () => {
  const participantsStatuses = ref(new Map<string, ContactMediaStatuses>())
  const conferenceCalls = ref(new Map<string, ConferenceCall[]>())
  const participantsStatusesByRoom = ref(new Map<string, Map<string, ContactMediaStatuses>>())
  const locallyMutedAudio = ref(new Map<string, boolean>())
  const handEnabledByRoom = ref(new Map<string, Map<string, boolean>>())

  const currentUserVideoStreamsByCallId = ref(new Map<string, ContactInCallStreamState[]>())

  const getParticipantMediaStatus = (callId: string): ContactMediaStatuses | undefined => participantsStatuses.value.get(callId)

  const removeStatusesByCallId = (callId: string) => {
    participantsStatuses.value.delete(callId)
    currentUserVideoStreamsByCallId.value.delete(callId)
    locallyMutedAudio.value.delete(callId)
  }

  const updateStatusesFromContactStatus = (contacts: Array<ContactState>, pServed: string) => {
    const { currentUser } = useCurrentUser()
    const { currentGuest } = useGuestAuth()

    const currentPServedUser = currentUser.value?.imLogin ?? currentGuest.value?.pServed
    if (!currentPServedUser) {
      return
    }

    for (const contact of contacts) {
      if (!handEnabledByRoom.value.has(pServed)) {
        handEnabledByRoom.value.set(pServed, new Map())
      }
      handEnabledByRoom.value.get(pServed)?.set(contact.id, contact.handEnabled)

      if (!contact.calls) {
        continue
      }

      for (const call of contact.calls) {
        const audioStream = call.streams?.find(stream => stream.kind === 'audio' && stream.clientNameByServer === 'audio' && stream.clientBound)
        const videoStream = call.streams?.find(stream => stream.kind === 'video' && stream.clientNameByServer === WEBCAM_STREAM_NAME && stream.clientBound)
        const screenSharingStream = call.streams?.find(stream => stream.kind === 'video' && stream.clientNameByServer === SCREEN_SHARING_STREAM_NAME && stream.clientBound)

        const status: ContactMediaStatuses = {
          id: contact.id,
          handEnabled: contact.handEnabled,
          isAudioMuted: !(audioStream ? audioStream.clientEnabled : false),
          isVideoMuted: !(videoStream ? videoStream.clientEnabled : false),
          hasVideo: !!videoStream,
          isScreenSharingMuted: !(screenSharingStream ? screenSharingStream.clientEnabled : false),
        }

        if (contact.id === currentPServedUser) {
          currentUserVideoStreamsByCallId.value.set(call.id, call.streams?.filter(stream => stream.kind === 'video' && stream.clientBound && (stream.clientNameByServer === WEBCAM_STREAM_NAME || stream.clientNameByServer === SCREEN_SHARING_STREAM_NAME)) ?? [])
        }

        participantsStatuses.value.set(call.id, status)

        if (!participantsStatusesByRoom.value.has(pServed)) {
          participantsStatusesByRoom.value.set(pServed, new Map())
        }
        participantsStatusesByRoom.value.get(pServed)?.set(call.id, status)
      }
    }
  }

  const updateConferenceCallsFromContactStatus = (roomPServed: string, contacts: Array<ContactState>) => {
    const participants: ConferenceCall[] = []
    for (const { id: participantPServed, calls } of contacts) {
      if (!calls) {
        continue
      }

      for (const { id, userAgent, terminalType, shortId } of calls) {
        participants.push({
          callId: id,
          participantPServed,
          userAgent,
          terminalType,
          shortCallId: shortId,
        })
      }
    }

    conferenceCalls.value.set(roomPServed, participants)
  }

  const getConferenceParticipants = (room: Room): ConferenceParticipant[] => {
    const subscribers: RoomSubscriberWithId[] = getParticipantsFromRoom(room)
    const participants: ConferenceParticipant[] = []
    const { contactsUserAgents } = useStoreContacts()
    conferenceCalls.value.get(room.pServedUser)?.forEach(call => {
      const subscriber = subscribers.find(s => s.participantId === call.participantPServed)
      if (subscriber) {
        participants.push({
          ...subscriber.contact,
          ...call,
          userAgent: resolveConferenceUserAgent(
            call.userAgent,
            contactsUserAgents.get(call.participantPServed),
          ),
        })
      }
    })

    return participants
  }

  const getConferenceParticipantsWithoutDialingRepeating = (room: Room): ConferenceParticipant[] => {
    const result: ConferenceParticipant[] = []
    const participants = getConferenceParticipants(room)

    const { getParticipantStatus } = useIntercomRoomParticipantsStatusesStore()
    participants.forEach(participant => {
      const statusLookupId = participant.internalNumber || fromPServedUser(participant.participantPServed)
      const status = getParticipantStatus(room.pServedUser, statusLookupId, participant.callId)
      if (status === MemberStatus.Dialing && !!result.find(p => p.participantPServed === participant.participantPServed)) {
        return
      }

      result.push(participant)
    })

    return result
  }

  const getConferenceCallsByImLogin = (room: Room, imLoginOrParticipantId: string) =>
    getConferenceParticipants(room).filter(p => p.imLogin === imLoginOrParticipantId || p.participantPServed === imLoginOrParticipantId)

  const getCurrentUserHasWebCamStreamByCallId = (callId: string) => {
    const currentVideoStreams = currentUserVideoStreamsByCallId.value.get(callId)
    return !!currentVideoStreams?.find(stream => stream.clientNameByServer === WEBCAM_STREAM_NAME)
  }

  const getCurrentUserHasScreenSharingStreamByCallId = (callId: string) => {
    const currentVideoStreams = currentUserVideoStreamsByCallId.value.get(callId)
    return !!currentVideoStreams?.find(stream => stream.clientNameByServer === SCREEN_SHARING_STREAM_NAME)
  }

  const getStatusesByRoom = (pServed: string) => participantsStatusesByRoom.value.get(pServed)

  const getStatusesByRoomAndPServed = (roomPServed: string, pServed: string) => Array.from(getStatusesByRoom(roomPServed)?.values() ?? []).filter(s => s.id === pServed)
  const setIsLocallyMutedAudio = (callId: string, value: boolean): void => {
    locallyMutedAudio.value.set(callId, value)
  }

  const getIsLocallyMutedAudio = (callId: string): boolean => locallyMutedAudio.value.get(callId) ?? false

  const getContactsAwaitingForAdding = (room: Room): AwaitingContact[] => {
    const { currentUser } = useCurrentUser()
    const contact = room.contacts?.find(c => c.id === currentUser.value?.imLogin)
    if (room.mode === ConferenceType.FREE_FOR_ALL || contact?.role === 'User') {
      return []
    }

    const { getParticipationRequestsByRoomPServed } = useModeratedConferenceParticipantsSignalsStore()
    const requests = getParticipationRequestsByRoomPServed(room.pServedUser)
    if (!requests?.size) return []

    const contacts: AwaitingContact[] = []
    Array.from(requests.keys()).forEach(uid => {
      const request = requests.get(uid)
      if (request) {
        const pServed = toPServedUser(request.targetNumber, false)
        const contact = useStoreContacts().findByPServed(pServed)
        if (contact) {
          contacts.push({
            ...contact,
            uid,
          })
        }
      }
    })

    return contacts
  }

  useIM().onConferenceUpdated((event: GroupStatusEvent) => {
    const { extendedParams: { contacts }, pServed } = event
    if (!contacts?.length || !pServed) {
      return
    }

    updateStatusesFromContactStatus(contacts, pServed)
    updateConferenceCallsFromContactStatus(pServed, contacts)
  })

  eventBus.on('callEnded', (callId: string) => {
    removeStatusesByCallId(callId)
  })

  return {
    getParticipantMediaStatus,
    getCurrentUserHasWebCamStreamByCallId,
    getCurrentUserHasScreenSharingStreamByCallId,
    getConferenceParticipants,
    getConferenceCallsByImLogin,
    getStatusesByRoom,
    getStatusesByRoomAndPServed,
    getConferenceParticipantsWithoutDialingRepeating,
    setIsLocallyMutedAudio,
    getIsLocallyMutedAudio,
    getContactsAwaitingForAdding,
  }
})
